import { fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import { baseurls } from "../_config/apiRoutes";
import { classifyError, DEFAULT_MESSAGES, ERROR_KINDS } from "../_utils/apiError";
import { signOut } from "../_features/auth/state/authSlice";
import { logoutCustomer } from "../_features/pay/state/customerPortalSlice";
import { markServerReachable, markServerUnreachable } from "./networkSlice";

// Shared by apiSlice.js and exportApiSlice.js - the one place that owns
// token selection, timeouts, error classification, global 401 handling,
// and retry for every API call in the app.

const DEFAULT_TIMEOUT_MS = 30000;

// Routes that only ever accept a customer-portal ("customerLogin") token -
// the server-side middleware guarding them (customerPortalAuth.js) rejects
// any other token type outright with a 401. D15 - every endpoint in the app
// shares this one base query, and it used to prefer an org/staff token over
// the customer's whenever both happened to be present in Redux state (e.g.
// an admin who created the test upsell also has the billing portal open in
// the same browser to check it, a completely ordinary way to test this).
// That silently 401'd every customer-portal request - including the
// Approvals tab's GET /customerapprovals - which read as "never populates"
// rather than a visible auth error.
const CUSTOMER_PORTAL_ONLY_PATHS = ["/customer-portal/", "/customerapprovals"];

const isCustomerPortalOnlyUrl = (url) =>
  typeof url === "string" && CUSTOMER_PORTAL_ONLY_PATHS.some((prefix) => url.includes(prefix));

// A 401 from any of these means "wrong credentials", not "your session
// expired" - they're the only endpoints callable without an existing valid
// token. Everything else behind auth (including /auth/changeorg, which DOES
// require a valid token) treats a 401 as a dead session.
const AUTH_EXEMPT_PATHS = [
  "/auth/login",
  "/auth/otp",
  "/auth/validateinvitation",
  "/auth/resendinvitation",
  "/customer-portal/login",
  "/customer-portal/generate-otp",
];

const isAuthExemptUrl = (url) =>
  typeof url === "string" && AUTH_EXEMPT_PATHS.some((prefix) => url.includes(prefix));

// prepareHeaders never receives the request URL, so the two possible token
// strategies have to be two separate fetchBaseQuery instances built once at
// module scope (not per-request - the previous per-request construction in
// apiSlice.js recreated one of these on every single call).
const makeFetcher = (pickToken) =>
  fetchBaseQuery({
    baseUrl: baseurls.main,
    credentials: "include",
    timeout: DEFAULT_TIMEOUT_MS,
    prepareHeaders: (headers, { getState }) => {
      const token = pickToken(getState());
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

const customerPortalFetcher = makeFetcher((state) => state.customerPortal.token);
// Every other route keeps the original precedence: org user token first,
// customer token as fallback.
const defaultFetcher = makeFetcher((state) => state.auth.token || state.customerPortal.token);

const handleUnauthorized = (api, url, customerPortalOnly) => {
  if (isAuthExemptUrl(url)) return;
  const state = api.getState();
  if (customerPortalOnly || !state.auth.token) {
    // A parallel storm of requests can all 401 at once - only the first
    // actually needs to dispatch, this keeps the rest as no-ops.
    if (state.customerPortal.token) api.dispatch(logoutCustomer());
  } else if (state.auth.token) {
    api.dispatch(signOut());
  }
  // No imperative navigation here - PrivateRoute already redirects to
  // sign-in the moment auth.token is falsy.
};

const instrumentedBaseQuery = async (args, api, extraOptions) => {
  const requestUrl = typeof args === "string" ? args : args?.url;
  const customerPortalOnly = isCustomerPortalOnlyUrl(requestUrl);
  const fetcher = customerPortalOnly ? customerPortalFetcher : defaultFetcher;

  const result = await fetcher(args, api, extraOptions);

  if (!result.error) {
    // Only dispatch on an actual transition, not on every single success -
    // otherwise every request in the app writes to the store.
    if (api.getState().network.isServerReachable === false) {
      api.dispatch(markServerReachable());
    }
    return result;
  }

  const status = result.error.status;
  const kind = classifyError(status);

  if (kind === ERROR_KINDS.NETWORK || kind === ERROR_KINDS.TIMEOUT) {
    api.dispatch(markServerUnreachable());
  }
  if (kind === ERROR_KINDS.UNAUTHORIZED) {
    handleUnauthorized(api, requestUrl, customerPortalOnly);
  }

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error("API Error:", kind, result.error);
  }

  const serverData =
    result.error?.data && typeof result.error.data === "object" ? result.error.data : null;

  return {
    error: {
      status: status ?? "FETCH_ERROR",
      kind,
      originalStatus: result.error?.originalStatus,
      data: {
        ...serverData,
        message: serverData?.message || DEFAULT_MESSAGES[kind],
      },
    },
  };
};

const RETRYABLE_KINDS = new Set([ERROR_KINDS.NETWORK, ERROR_KINDS.TIMEOUT, ERROR_KINDS.SERVER]);

// Only GET queries retry, and only for failure kinds where a second attempt
// might actually succeed. Mutations never retry here - resending a
// create/update/delete after an ambiguous failure risks doing it twice.
export const baseQueryWithResilience = retry(instrumentedBaseQuery, {
  maxRetries: 2,
  retryCondition: (error, args, { attempt, baseQueryApi }) => {
    if (attempt > 2) return false;
    if (baseQueryApi.type !== "query") return false;
    const method = (typeof args === "object" && args?.method) || "GET";
    if (method !== "GET") return false;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
    return RETRYABLE_KINDS.has(error?.kind);
  },
});
